import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapHoriz as TradeIcon,
} from '@mui/icons-material';
import { usePlayer } from '../contexts/PlayerContext';
import { useUser } from '../contexts/UserContext';
import { playerService, Player, User, FantasyTeam, FantasyTeamPlayer, PlayerGame } from '../services/playerService';

const PlayerDialog: React.FC = () => {
  const { selectedPlayer, isDialogOpen, closePlayerDialog } = usePlayer();
  const { currentUsername } = useUser();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userFantasyTeams, setUserFantasyTeams] = useState<FantasyTeam[]>([]);
  const [isOnTeam, setIsOnTeam] = useState(false);
  const [rosterPlayer, setRosterPlayer] = useState<FantasyTeamPlayer | null>(null);
  const [playerOwner, setPlayerOwner] = useState<{
    isOwned: boolean;
    teamName: string | null;
    ownerName: string | null;
    fantasyTeamId: number | null;
  } | null>(null);
  const [checkingOwner, setCheckingOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingTeam, setCheckingTeam] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [realStats, setRealStats] = useState<{
    gamesPlayed: number;
    pointsPerGame: number;
    reboundsPerGame: number;
    assistsPerGame: number;
    stealsPerGame: number;
    blocksPerGame: number;
    turnoversPerGame: number;
    fantasyPointsPerGame: number;
    fieldGoalPercentage: number;
    threePointPercentage: number;
    freeThrowPercentage: number;
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [activeTab, setActiveTab] = useState(0);
  const [playerGames, setPlayerGames] = useState<PlayerGame[]>([]);
  const [loadingGames, setLoadingGames] = useState(false);

  const fantasyPointsPerGame = selectedPlayer?.fantasyPointsPerGame ?? realStats?.fantasyPointsPerGame ?? null;

  // Get primary team (first team)
  const primaryTeam = userFantasyTeams.length > 0 ? userFantasyTeams[0] : null;

  // Check if player is on team and check ownership
  useEffect(() => {
    const checkPlayerOnTeam = async () => {
      if (!selectedPlayer || !currentUser) {
        setIsOnTeam(false);
        setRosterPlayer(null);
        setCheckingTeam(false);
        return;
      }

      // If no primary team yet, don't check but also don't keep checking state
      if (!primaryTeam) {
        setIsOnTeam(false);
        setRosterPlayer(null);
        setCheckingTeam(false);
        return;
      }

      setCheckingTeam(true);
      try {
        // Get team players
        const teamPlayers = await playerService.getTeamPlayers(primaryTeam.id);
        const foundPlayer = teamPlayers.find(tp => tp.playerId === selectedPlayer.id);
        
        if (foundPlayer) {
          setIsOnTeam(true);
          setRosterPlayer(foundPlayer);
        } else {
          setIsOnTeam(false);
          setRosterPlayer(null);
        }
      } catch (error) {
        console.error('Error checking if player is on team:', error);
        setIsOnTeam(false);
        setRosterPlayer(null);
      } finally {
        setCheckingTeam(false);
      }
    };

    if (isDialogOpen && selectedPlayer) {
      checkPlayerOnTeam();
    } else {
      // Reset checking state when dialog closes
      setCheckingTeam(false);
    }
  }, [selectedPlayer, currentUser, primaryTeam, isDialogOpen]);

  // Check player ownership (which team owns the player)
  useEffect(() => {
    const checkPlayerOwner = async () => {
      if (!selectedPlayer || !isDialogOpen) {
        setPlayerOwner(null);
        return;
      }

      setCheckingOwner(true);
      try {
        const owner = await playerService.getPlayerOwner(selectedPlayer.id);
        setPlayerOwner(owner);
        
        // If player is owned, also update isOnTeam status if it's our team
        if (owner.isOwned && primaryTeam && owner.fantasyTeamId === primaryTeam.id) {
          setIsOnTeam(true);
        } else if (owner.isOwned && primaryTeam && owner.fantasyTeamId !== primaryTeam.id) {
          setIsOnTeam(false);
        }
      } catch (error) {
        console.error('Error checking player owner:', error);
        setPlayerOwner(null);
      } finally {
        setCheckingOwner(false);
      }
    };

    if (isDialogOpen && selectedPlayer) {
      checkPlayerOwner();
    }
  }, [selectedPlayer, isDialogOpen, primaryTeam]);

  // Load user's fantasy teams when dialog opens
  useEffect(() => {
    const loadFantasyTeams = async () => {
      if (!currentUsername || !isDialogOpen) {
        setUserFantasyTeams([]);
        setCurrentUser(null);
        return;
      }

      try {
        // Load user by username first
        const user = await playerService.getUserByUsername(currentUsername);
        setCurrentUser(user);
        
        if (!user) {
          console.warn('PlayerDialog: User not found for username:', currentUsername);
          setUserFantasyTeams([]);
          return;
        }

        const teams = await playerService.getFantasyTeamsByUser(user.id);
        console.log('PlayerDialog: Loaded fantasy teams:', teams);
        setUserFantasyTeams(teams);
      } catch (error) {
        console.error('Error loading fantasy teams:', error);
        setUserFantasyTeams([]);
        setCurrentUser(null);
      }
    };

    if (isDialogOpen) {
      loadFantasyTeams();
    } else {
      // Reset teams and user when dialog closes
      setUserFantasyTeams([]);
      setCurrentUser(null);
    }
  }, [currentUsername, isDialogOpen]);

  // Load player games when dialog opens
  useEffect(() => {
    const loadPlayerGames = async () => {
      if (!selectedPlayer || !isDialogOpen) {
        setPlayerGames([]);
        return;
      }

      setLoadingGames(true);
      try {
        const games = await playerService.getPlayerGames(selectedPlayer.id);
        console.log('PlayerDialog: Loaded player games:', games);
        setPlayerGames(games);
      } catch (error) {
        console.error('Error loading player games:', error);
        setPlayerGames([]);
      } finally {
        setLoadingGames(false);
      }
    };

    if (isDialogOpen) {
      loadPlayerGames();
    } else {
      setPlayerGames([]);
      setActiveTab(0); // Reset to first tab when dialog closes
    }
  }, [selectedPlayer, isDialogOpen]);

  // Load real player statistics from database when dialog opens
  useEffect(() => {
    const loadPlayerStats = async () => {
      if (!selectedPlayer || !isDialogOpen) {
        setRealStats(null);
        return;
      }

      setLoadingStats(true);
      try {
        const stats = await playerService.getPlayerStats(selectedPlayer.id);
        setRealStats(stats);
      } catch (error) {
        console.error('Error loading player statistics:', error);
        setRealStats(null);
      } finally {
        setLoadingStats(false);
      }
    };

    if (isDialogOpen && selectedPlayer) {
      loadPlayerStats();
    }
  }, [selectedPlayer, isDialogOpen]);

  const getPlayerInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getPositionColor = (position: string) => {
    const colors: { [key: string]: string } = {
      'PG': '#FF6B35', 'SG': '#2C3E50', 'SF': '#27AE60',
      'PF': '#F39C12', 'C': '#E74C3C', 'G': '#3498DB', 'F': '#9B59B6'
    };
    return colors[position] || '#95A5A6';
  };

  const formatMinutes = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAddPlayer = async () => {
    if (!selectedPlayer || !primaryTeam) {
      setSnackbarMessage('No player or team selected.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Check if player is already owned by another team
    if (playerOwner?.isOwned && playerOwner.fantasyTeamId !== primaryTeam.id) {
      setSnackbarMessage(`❌ ${selectedPlayer.name} is already owned by ${playerOwner.teamName}. The player must be dropped from that team first.`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      await playerService.addPlayerToTeam(primaryTeam.id, selectedPlayer.id);
      setIsOnTeam(true);
      // Refresh ownership info
      const owner = await playerService.getPlayerOwner(selectedPlayer.id);
      setPlayerOwner(owner);
      
      setSnackbarMessage(`✅ ${selectedPlayer.name} added to ${primaryTeam.name}!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Trigger a page refresh by dispatching a custom event
      window.dispatchEvent(new CustomEvent('playerAdded'));
    } catch (error) {
      console.error('Error adding player to team:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Show user-friendly error messages
      if (errorMessage.includes('already owned')) {
        setSnackbarMessage(`❌ ${errorMessage}`);
      } else {
        setSnackbarMessage(`❌ Failed to add ${selectedPlayer.name}: ${errorMessage}`);
      }
      
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      
      // Refresh ownership info in case it changed
      try {
        const owner = await playerService.getPlayerOwner(selectedPlayer.id);
        setPlayerOwner(owner);
      } catch (err) {
        console.error('Error refreshing player owner:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlayer = async () => {
    if (!selectedPlayer || !primaryTeam) {
      setSnackbarMessage('No player or team selected.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      await playerService.removePlayerFromTeam(primaryTeam.id, selectedPlayer.id);
      setIsOnTeam(false);
      setRosterPlayer(null);
      
      // Refresh ownership info after removal
      const owner = await playerService.getPlayerOwner(selectedPlayer.id);
      setPlayerOwner(owner);
      
      setSnackbarMessage(`✅ ${selectedPlayer.name} removed from ${primaryTeam.name}!`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Trigger a page refresh by dispatching a custom event
      window.dispatchEvent(new CustomEvent('playerRemoved'));
    } catch (error) {
      console.error('Error removing player from team:', error);
      setSnackbarMessage(`❌ Failed to remove ${selectedPlayer.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedPlayer) return null;

  return (
    <>
      <Dialog 
        open={isDialogOpen} 
        onClose={closePlayerDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: getPositionColor(selectedPlayer.position),
                width: 56, 
                height: 56,
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}
            >
              {getPlayerInitials(selectedPlayer.name)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {selectedPlayer.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlayer.team} • {selectedPlayer.position}
                {selectedPlayer.jerseyNumber && ` • #${selectedPlayer.jerseyNumber}`}
              </Typography>
            </Box>
            {checkingTeam || checkingOwner ? (
              <CircularProgress size={24} />
            ) : isOnTeam ? (
              <Chip 
                label="On Your Team" 
                color="success" 
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            ) : playerOwner?.isOwned && playerOwner.teamName ? (
              <Chip 
                label={`Owned by ${playerOwner.teamName}`} 
                color="warning" 
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            ) : null}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Overview" />
            <Tab label="Recent Games" />
          </Tabs>
          
          <Box sx={{ py: 2, px: 2 }}>
            {activeTab === 0 && (
              <Box>
            {/* Fantasy Points */}
            <Box sx={{ mb: 3, p: 3, bgcolor: 'primary.main', borderRadius: 2, color: 'white' }}>
              <Typography variant="body2" sx={{ display: 'block', mb: 1, color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'medium' }}>
                Fantasy Points Per Game {realStats && `(${realStats.gamesPlayed} games)`}
              </Typography>
              {loadingStats ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'white' }}>
                  {fantasyPointsPerGame !== null ? fantasyPointsPerGame.toFixed(1) : 'N/A'}
                </Typography>
              )}
            </Box>

            {/* Stats */}
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
              Statistics (Per Game)
            </Typography>
            {loadingStats ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 2.4 }}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>MIN</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {(() => {
                          if (playerGames.length > 0) {
                            const totalMinutes = playerGames.reduce((sum, game) => sum + game.minutesPlayed, 0);
                            const avgMinutes = totalMinutes / playerGames.length;
                            const mins = Math.floor(avgMinutes / 60);
                            const secs = Math.round(avgMinutes % 60);
                            return `${mins}:${secs.toString().padStart(2, '0')}`;
                          }
                          return 'N/A';
                        })()}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>PTS</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {realStats ? realStats.pointsPerGame.toFixed(1) : (selectedPlayer.pointsPerGame?.toFixed(1) || 'N/A')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>REB</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {realStats ? realStats.reboundsPerGame.toFixed(1) : (selectedPlayer.reboundsPerGame?.toFixed(1) || 'N/A')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>AST</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {realStats ? realStats.assistsPerGame.toFixed(1) : (selectedPlayer.assistsPerGame?.toFixed(1) || 'N/A')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 2.4 }}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>STL</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {realStats ? realStats.stealsPerGame.toFixed(1) : (selectedPlayer.stealsPerGame?.toFixed(1) || 'N/A')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Additional stats */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>BLK</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {realStats ? realStats.blocksPerGame.toFixed(1) : (selectedPlayer.blocksPerGame?.toFixed(1) || 'N/A')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>FG%</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {realStats ? realStats.fieldGoalPercentage.toFixed(1) : (selectedPlayer.fieldGoalPercentage?.toFixed(1) || 'N/A')}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>3P%</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {realStats ? realStats.threePointPercentage.toFixed(1) : (selectedPlayer.threePointPercentage?.toFixed(1) || 'N/A')}%
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Ownership info - only show if owned by another team */}
            {playerOwner && playerOwner.isOwned && playerOwner.teamName && primaryTeam && playerOwner.fantasyTeamId !== primaryTeam.id && (
              <>
                <Divider sx={{ my: 2 }} />
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Player Already Owned
                  </Typography>
                  <Typography variant="body2">
                    This player is currently owned by <strong>{playerOwner.teamName}</strong> ({playerOwner.ownerName}).
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    The player must be dropped from that team before you can add them to your team.
                  </Typography>
                </Alert>
              </>
            )}

            {/* Player details */}
            {(selectedPlayer.height || selectedPlayer.weight || selectedPlayer.nationality) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                  Player Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {selectedPlayer.height && (
                    <Typography variant="body2" color="text.secondary">
                      Height: {selectedPlayer.height}m
                    </Typography>
                  )}
                  {selectedPlayer.weight && (
                    <Typography variant="body2" color="text.secondary">
                      Weight: {selectedPlayer.weight}kg
                    </Typography>
                  )}
                  {selectedPlayer.nationality && (
                    <Typography variant="body2" color="text.secondary">
                      Nationality: {selectedPlayer.nationality}
                    </Typography>
                  )}
                </Box>
              </>
            )}
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                {loadingGames ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : playerGames.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No games found for this player.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '50vh' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Opponent</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>MIN</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>PTS</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>REB</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>OREB</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>DREB</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>AST</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>STL</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>BLK</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>TO</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>FPTS</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {playerGames.map((game) => {
                          const gameDate = game.gameDate ? new Date(game.gameDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A';
                          const isHome = game.homeTeam === selectedPlayer.team;
                          const opponent = isHome ? game.awayTeam : game.homeTeam;
                          const homeScore = game.homeTeamScore ?? null;
                          const awayScore = game.awayTeamScore ?? null;
                          const playerTeamScore = isHome ? homeScore : awayScore;
                          const opponentScore = isHome ? awayScore : homeScore;
                          const gameResult = homeScore !== null && awayScore !== null
                            ? (isHome ? (homeScore > awayScore ? 'W' : 'L') : (awayScore > homeScore ? 'W' : 'L'))
                            : null;

                          return (
                            <TableRow key={game.id} hover>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {gameDate}
                                  </Typography>
                                  {gameResult && (
                                    <Typography variant="caption" color={gameResult === 'W' ? 'success.main' : 'error.main'}>
                                      {gameResult} {playerTeamScore}-{opponentScore}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {isHome ? 'vs' : '@'} {opponent}
                                </Typography>
                                {game.venue && (
                                  <Typography variant="caption" color="text.secondary">
                                    {game.venue}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {Math.floor(game.minutesPlayed / 60)}:{(game.minutesPlayed % 60).toString().padStart(2, '0')}
                              </TableCell>
                              <TableCell align="center">{game.points}</TableCell>
                              <TableCell align="center">{game.rebounds}</TableCell>
                              <TableCell align="center">{game.offensiveRebounds}</TableCell>
                              <TableCell align="center">{game.defensiveRebounds}</TableCell>
                              <TableCell align="center">{game.assists}</TableCell>
                              <TableCell align="center">{game.steals}</TableCell>
                              <TableCell align="center">{game.blocks}</TableCell>
                              <TableCell align="center">{game.turnovers}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {game.fantasyPoints.toFixed(1)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={closePlayerDialog} disabled={loading}>
            Close
          </Button>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 180, justifyContent: 'flex-end' }}>
            {isOnTeam && primaryTeam ? (
              <Button
                onClick={handleRemovePlayer}
                variant="contained"
                color="error"
                startIcon={<RemoveIcon />}
                disabled={loading || checkingTeam || checkingOwner}
              >
                {loading || checkingTeam || checkingOwner ? <CircularProgress size={20} /> : 'Remove from Team'}
              </Button>
            ) : playerOwner?.isOwned && playerOwner.fantasyTeamId && playerOwner.fantasyTeamId !== primaryTeam?.id ? (
              <Button
                onClick={() => {
                  // Trade functionality - to be implemented
                  setSnackbarMessage('Trade functionality coming soon!');
                  setSnackbarSeverity('info');
                  setSnackbarOpen(true);
                }}
                variant="contained"
                color="warning"
                startIcon={<TradeIcon />}
                disabled={loading || checkingTeam || checkingOwner}
              >
                {(loading || checkingTeam || checkingOwner) ? <CircularProgress size={20} /> : 'Trade for Player'}
              </Button>
            ) : (
              <Button
                onClick={handleAddPlayer}
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={loading || checkingTeam || checkingOwner || !primaryTeam}
                title={!primaryTeam ? 'Create a fantasy team first' : (checkingTeam || checkingOwner ? 'Checking player status...' : '')}
              >
                {(loading || checkingTeam || checkingOwner) ? <CircularProgress size={20} /> : 'Add to Team'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PlayerDialog;

